import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mic2, Youtube, Upload, LogOut, Phone, Lock, Eye, EyeOff, Copy, Check, Clock, Rocket, Calendar, Users, Trash2, Star, Share2, Wallet, Megaphone, AlertTriangle, CreditCard, Camera, Loader2, CheckCircle, AlertCircle, Building2, Banknote, Radio, Music, Plus, Send, MessageCircle, Coins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [artistToken, setArtistToken] = useState(localStorage.getItem("e1_artist_token"));
  const [currentArtist, setCurrentArtist] = useState(null);
  const [myVideos, setMyVideos] = useState([]);
  const [myLaunches, setMyLaunches] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showUploadVideo, setShowUploadVideo] = useState(false);
  const [showCreateLaunch, setShowCreateLaunch] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [authTab, setAuthTab] = useState("login");
  const [selectedArtist, setSelectedArtist] = useState(null);

  const [loginForm, setLoginForm] = useState({ phone: "", password: "" });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");
  const [registerForm, setRegisterForm] = useState({
    name: "",
    phone: "",
    password: "",
    bio: "",
    photo_url: "",
    youtube_channel_url: ""
  });

  const [videoForm, setVideoForm] = useState({
    title: "",
    youtube_url: "",
    thumbnail_url: "",
    description: ""
  });

  const [launchForm, setLaunchForm] = useState({
    title: "",
    description: "",
    youtube_url: "",
    launch_date: "",
    launch_time: ""
  });

  const [showAddBalance, setShowAddBalance] = useState(false);
  const [showShareCampaign, setShowShareCampaign] = useState(false);
  const [addBalanceAmount, setAddBalanceAmount] = useState("");
  const [myShareCampaigns, setMyShareCampaigns] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  
  const [shareCampaignForm, setShareCampaignForm] = useState({
    title: "",
    description: "",
    share_url: "",
    platform: "instagram"
  });

  // Payment system states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentType, setPaymentType] = useState(null);  // 'youtube' or 'share'
  const [myPayments, setMyPayments] = useState([]);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState(null);
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const receiptInputRef = useRef(null);

  // Membership ($300 RD/month) states
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [membershipMonths, setMembershipMonths] = useState("1");
  const [membershipPayType, setMembershipPayType] = useState("membership");
  const [requestedPoints, setRequestedPoints] = useState("");
  const [submittingMembership, setSubmittingMembership] = useState(false);
  const membershipFileInputRef = useRef(null);
  const pointsFileInputRef = useRef(null);
  // null = auto (hidden when paid, shown when not paid); true/false = manual override
  const [paymentFormOpen, setPaymentFormOpen] = useState(null);
  const [pointsPurchaseOpen, setPointsPurchaseOpen] = useState(false);

  // Radio submission states
  const [showRadioSubmit, setShowRadioSubmit] = useState(false);
  const [radioSubmitForm, setRadioSubmitForm] = useState({ title: "", file: null });
  const [myRadioSubmissions, setMyRadioSubmissions] = useState([]);
  const [uploadingRadio, setUploadingRadio] = useState(false);
  const [currentRadioSubmission, setCurrentRadioSubmission] = useState(null);
  const radioFileInputRef = useRef(null);
  const radioProofInputRef = useRef(null);
  
  // Social Links state
  const [showSocialLinkSubmit, setShowSocialLinkSubmit] = useState(false);
  const [socialLinkForm, setSocialLinkForm] = useState({ platform: "instagram", username: "", profile_url: "", description: "" });
  const [mySocialLinks, setMySocialLinks] = useState([]);
  const [submittingSocialLink, setSubmittingSocialLink] = useState(false);

  // Song/Video link submission state
  const [showLinkSubmit, setShowLinkSubmit] = useState(false);
  const [linkForm, setLinkForm] = useState({ title: "", youtube_url: "" });
  const [myLinkSubmissions, setMyLinkSubmissions] = useState([]);
  const [submittingLink, setSubmittingLink] = useState(false);

  // Engagement store (spend points) state
  const ENGAGEMENT_UNIT = { subscribers: 25, comments: 5, likes: 3, visits: 1 };
  const ENGAGEMENT_COMBO = 125;
  const [engagementForm, setEngagementForm] = useState({ link: "", combos: 0, subscribers: 0, comments: 0, likes: 0, visits: 0 });
  const [myEngagementOrders, setMyEngagementOrders] = useState([]);
  const [submittingEngagement, setSubmittingEngagement] = useState(false);

  useEffect(() => {
    fetchArtists();
    fetchPaymentAccounts();
    fetchWhatsappConfig();
    if (artistToken) {
      fetchCurrentArtist();
      fetchMyLaunches();
      fetchMyShareCampaigns();
      fetchMyPayments();
      fetchMyRadioSubmissions();
      fetchMySocialLinks();
      fetchMembershipStatus();
      fetchMyLinkSubmissions();
      fetchMyEngagementOrders();
    }
  }, [artistToken]);

  const fetchPaymentAccounts = async () => {
    try {
      const response = await axios.get(`${API}/config/payment-accounts`);
      setPaymentAccounts(response.data);
    } catch (error) {
      console.error("Error fetching payment accounts:", error);
    }
  };

  const fetchWhatsappConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/whatsapp`);
      setWhatsappConfig(response.data);
    } catch (error) {
      console.error("Error fetching whatsapp config:", error);
    }
  };

  const openWhatsAppInfo = (topic) => {
    const number = whatsappConfig?.number;
    if (!number) {
      toast.error("Número de WhatsApp no disponible por ahora");
      return;
    }
    const msg = `Hola, soy artista de LEYBA PRO y quiero información sobre: ${topic}`;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API}/artists`);
      setArtists(response.data);
    } catch (error) {
      console.error("Error fetching artists:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentArtist = async () => {
    try {
      const response = await axios.get(`${API}/artists/me`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setCurrentArtist(response.data);
      fetchMyVideos();
    } catch (error) {
      console.error("Error fetching artist:", error);
      handleLogout();
    }
  };

  const fetchMyLaunches = async () => {
    try {
      const response = await axios.get(`${API}/launch-events/artist/my-events`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMyLaunches(response.data);
    } catch (error) {
      console.error("Error fetching launches:", error);
    }
  };

  const fetchMyShareCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/share-campaigns/artist/my-campaigns`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMyShareCampaigns(response.data);
    } catch (error) {
      console.error("Error fetching share campaigns:", error);
    }
  };

  const fetchMyPayments = async () => {
    try {
      const response = await axios.get(`${API}/artists/my-payments`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMyPayments(response.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
    }
  };

  const fetchMembershipStatus = async () => {
    try {
      const response = await axios.get(`${API}/membership/my-status`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMembershipStatus(response.data);
    } catch (error) {
      console.error("Error fetching membership status:", error);
    }
  };

  const handleSubmitMembership = async (file, isPointsPurchase = false) => {
    if (!file) return;
    setSubmittingMembership(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (isPointsPurchase) {
        formData.append("payment_type", "points");
        formData.append("requested_points", requestedPoints || "0");
        formData.append("months_count", "0");
      } else {
        formData.append("payment_type", "membership");
        formData.append("months_count", membershipMonths);
      }
      const response = await axios.post(`${API}/membership/submit`, formData, {
        headers: {
          Authorization: `Bearer ${artistToken}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(response.data.message);
      fetchMembershipStatus();
      if (isPointsPurchase) {
        setRequestedPoints("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar comprobante");
    } finally {
      setSubmittingMembership(false);
      if (membershipFileInputRef.current) membershipFileInputRef.current.value = "";
      if (pointsFileInputRef.current) pointsFileInputRef.current.value = "";
    }
  };

  const MEMBERSHIP_MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  const formatMembershipMonth = (m) => {
    if (!m) return "";
    const parts = m.split("-");
    return `${MEMBERSHIP_MONTH_NAMES[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
  };

  const fetchMyLinkSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/artist/link-submissions/my`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMyLinkSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching link submissions:", error);
    }
  };

  const handleSubmitLink = async () => {
    if (!linkForm.title.trim() || !linkForm.youtube_url.trim()) {
      toast.error("Completa el título y el enlace de YouTube");
      return;
    }
    setSubmittingLink(true);
    try {
      const response = await axios.post(`${API}/artist/link-submissions`, linkForm, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      toast.success(response.data.message);
      setLinkForm({ title: "", youtube_url: "" });
      setShowLinkSubmit(false);
      fetchMyLinkSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar el enlace");
    } finally {
      setSubmittingLink(false);
    }
  };

  const engagementTotal = (
    (Number(engagementForm.combos) || 0) * ENGAGEMENT_COMBO +
    (Number(engagementForm.subscribers) || 0) * ENGAGEMENT_UNIT.subscribers +
    (Number(engagementForm.comments) || 0) * ENGAGEMENT_UNIT.comments +
    (Number(engagementForm.likes) || 0) * ENGAGEMENT_UNIT.likes +
    (Number(engagementForm.visits) || 0) * ENGAGEMENT_UNIT.visits
  );

  const fetchMyEngagementOrders = async () => {
    try {
      const response = await axios.get(`${API}/artist/engagement/my-orders`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMyEngagementOrders(response.data);
    } catch (error) {
      console.error("Error fetching engagement orders:", error);
    }
  };

  const handleCreateEngagementOrder = async () => {
    if (!engagementForm.link.trim()) {
      toast.error("Indica el link de tu preferencia");
      return;
    }
    if (engagementTotal <= 0) {
      toast.error("Selecciona al menos un combo o cantidad");
      return;
    }
    setSubmittingEngagement(true);
    try {
      const payload = {
        link: engagementForm.link.trim(),
        combos: Number(engagementForm.combos) || 0,
        subscribers: Number(engagementForm.subscribers) || 0,
        comments: Number(engagementForm.comments) || 0,
        likes: Number(engagementForm.likes) || 0,
        visits: Number(engagementForm.visits) || 0,
      };
      const response = await axios.post(`${API}/artist/engagement/order`, payload, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      toast.success(response.data.message);
      setEngagementForm({ link: "", combos: 0, subscribers: 0, comments: 0, likes: 0, visits: 0 });
      fetchMyEngagementOrders();
      fetchCurrentArtist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear el pedido");
    } finally {
      setSubmittingEngagement(false);
    }
  };

  const fetchMyRadioSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/artist/radio/my-submissions`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMyRadioSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching radio submissions:", error);
    }
  };

  const handleSubmitRadioTrack = async () => {
    if (!radioSubmitForm.title || !radioSubmitForm.file) {
      toast.error("Completa el título y selecciona un archivo MP3");
      return;
    }
    
    setUploadingRadio(true);
    try {
      const formData = new FormData();
      formData.append("file", radioSubmitForm.file);
      formData.append("title", radioSubmitForm.title);
      
      const response = await axios.post(`${API}/artist/radio/submit`, formData, {
        headers: { 
          Authorization: `Bearer ${artistToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      setCurrentRadioSubmission(response.data);
      setRadioSubmitForm({ title: "", file: null });
      fetchMyRadioSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar canción");
    } finally {
      setUploadingRadio(false);
    }
  };

  const handleUploadRadioPaymentProof = async (submissionId, file) => {
    if (!file) return;
    
    setUploadingRadio(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API}/artist/radio/submit/${submissionId}/payment-proof`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${artistToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(response.data.message);
      fetchMyRadioSubmissions();
      setCurrentRadioSubmission(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir comprobante");
    } finally {
      setUploadingRadio(false);
    }
  };

  // Social Links Functions
  const fetchMySocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/artists/me/social-links`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMySocialLinks(response.data);
    } catch (error) {
      console.error("Error fetching social links:", error);
    }
  };

  const handleSubmitSocialLink = async () => {
    if (!socialLinkForm.username || !socialLinkForm.profile_url) {
      toast.error("Completa el nombre de usuario y la URL del perfil");
      return;
    }
    
    setSubmittingSocialLink(true);
    try {
      const response = await axios.post(
        `${API}/artists/social-links`,
        socialLinkForm,
        { headers: { Authorization: `Bearer ${artistToken}` } }
      );
      
      toast.success(response.data.message);
      setSocialLinkForm({ platform: "instagram", username: "", profile_url: "", description: "" });
      setShowSocialLinkSubmit(false);
      fetchMySocialLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar solicitud");
    } finally {
      setSubmittingSocialLink(false);
    }
  };

  const createPaymentRequest = async (campaignType, maxUsers = 0) => {
    try {
      const params = new URLSearchParams();
      params.append('campaign_type', campaignType);
      if (campaignType === 'share') {
        params.append('max_users', shareCampaignForm.max_users);
      }
      
      const response = await axios.post(`${API}/artists/create-campaign-payment?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      
      toast.success(response.data.message);
      setCurrentPayment(response.data);
      setPaymentType(campaignType);
      setShowPaymentDialog(true);
      fetchMyPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear solicitud de pago");
    }
  };

  const handleReceiptUpload = async (paymentId, file) => {
    if (!file) return;
    
    setUploadingReceipt(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(
        `${API}/artists/upload-payment-receipt/${paymentId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${artistToken}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      toast.success(response.data.message);
      fetchMyPayments();
      setShowPaymentDialog(false);
      setCurrentPayment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir comprobante");
    } finally {
      setUploadingReceipt(false);
    }
  };

  const fetchMyVideos = async () => {
    try {
      const response = await axios.get(`${API}/youtube-campaigns/artist/my-campaigns`, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      setMyVideos(response.data);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/artists/login`, loginForm);
      localStorage.setItem("e1_artist_token", response.data.token);
      setArtistToken(response.data.token);
      setCurrentArtist(response.data.artist);
      toast.success("¡Bienvenido!");
      setLoginForm({ phone: "", password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPhone.trim()) {
      toast.error("Ingresa tu número de teléfono");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/artist/request-password-reset?phone=${forgotPhone}`);
      const { whatsapp_number, artist_name } = response.data;
      const message = `Hola, soy el artista ${artist_name}. Necesito recuperar mi contraseña. Mi teléfono es: ${forgotPhone}`;
      const whatsappUrl = `https://wa.me/${whatsapp_number}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      toast.success("Contacta al administrador por WhatsApp para recuperar tu contraseña");
      setShowForgotPassword(false);
      setForgotPhone("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Artista no encontrado");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/artists/register`, registerForm);
      toast.success(res.data?.message || "Registro recibido. Pendiente de aprobación del administrador.");
      setAuthTab("login");
      setRegisterForm({ name: "", phone: "", password: "", bio: "", photo_url: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrarse");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("e1_artist_token");
    setArtistToken(null);
    setCurrentArtist(null);
    setMyVideos([]);
    setMyLaunches([]);
    toast.success("Sesión cerrada");
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten imágenes (JPG, PNG, WEBP)");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no puede superar 5MB");
      return;
    }
    
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(
        `${API}/artists/me/upload-photo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${artistToken}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );
      toast.success("Foto de perfil actualizada");
      // Update local state
      setCurrentArtist(prev => ({ ...prev, photo_url: response.data.photo_url }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir la foto");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  };

  const handleCreateLaunch = async (e) => {
    e.preventDefault();
    if (!launchForm.title || !launchForm.youtube_url || !launchForm.launch_date || !launchForm.launch_time) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }

    // Combine date and time into ISO format
    const launchDatetime = new Date(`${launchForm.launch_date}T${launchForm.launch_time}`).toISOString();

    try {
      const response = await axios.post(
        `${API}/launch-events`,
        {
          title: launchForm.title,
          description: launchForm.description,
          youtube_url: launchForm.youtube_url,
          launch_datetime: launchDatetime
        },
        { headers: { Authorization: `Bearer ${artistToken}` } }
      );
      
      toast.success(response.data.message);
      setShowCreateLaunch(false);
      setLaunchForm({ title: "", description: "", youtube_url: "", launch_date: "", launch_time: "" });
      fetchMyLaunches();
      fetchCurrentArtist(); // Refresh balance
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear lanzamiento");
    }
  };

  const handleCancelLaunch = async (eventId) => {
    try {
      const response = await axios.delete(
        `${API}/launch-events/${eventId}`,
        { headers: { Authorization: `Bearer ${artistToken}` } }
      );
      toast.success(response.data.message);
      fetchMyLaunches();
      fetchCurrentArtist(); // Refresh balance
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar lanzamiento");
    }
  };

  const handlePromoteLaunch = async (eventId) => {
    try {
      const response = await axios.post(
        `${API}/launch-events/${eventId}/promote`,
        {},
        { headers: { Authorization: `Bearer ${artistToken}` } }
      );
      toast.success(response.data.message);
      fetchMyLaunches();
      fetchCurrentArtist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al promocionar lanzamiento");
    }
  };

  const handleCreatePlan1 = async (eventId, youtubeChannelUrl) => {
    if (!youtubeChannelUrl) {
      const url = prompt("Ingresa la URL de tu canal de YouTube:");
      if (!url) return;
      youtubeChannelUrl = url;
    }
    
    try {
      const response = await axios.post(
        `${API}/launch-events/${eventId}/create-plan1?youtube_channel_url=${encodeURIComponent(youtubeChannelUrl)}`,
        {},
        { headers: { Authorization: `Bearer ${artistToken}` } }
      );
      toast.success(response.data.message);
      fetchMyLaunches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear Plan 1");
    }
  };

  const handleCreateShareCampaign = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/share-campaigns`,
        shareCampaignForm,
        { headers: { Authorization: `Bearer ${artistToken}` } }
      );
      toast.success(response.data.message);
      setShowShareCampaign(false);
      setShareCampaignForm({ title: "", description: "", share_url: "", platform: "instagram" });
      fetchMyShareCampaigns();
      fetchCurrentArtist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear campaña");
    }
  };

  const handleAddBalanceRequest = async () => {
    const amount = parseFloat(addBalanceAmount);
    if (!amount || amount <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/artists/add-balance?amount=${amount}`,
        {},
        { headers: { Authorization: `Bearer ${artistToken}` } }
      );
      toast.success(response.data.message);
      setShowAddBalance(false);
      setAddBalanceAmount("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al solicitar recarga");
    }
  };

  const handleUploadVideo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/youtube-campaigns`, videoForm, {
        headers: { Authorization: `Bearer ${artistToken}` }
      });
      toast.success("¡Campaña de Video creada exitosamente!");
      setShowUploadVideo(false);
      setVideoForm({ title: "", youtube_url: "", thumbnail_url: "", description: "" });
      fetchCurrentArtist();
      fetchMyVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear campaña");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D946EF]"></div>
      </div>
    );
  }

  // Artist Dashboard (logged in)
  if (artistToken && currentArtist) {
    return (
      <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="artist-dashboard">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Artist Header */}
          <Card className="bg-gradient-to-br from-[#D946EF]/20 to-[#8B5CF6]/20 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <div className="w-20 h-20 rounded-full bg-[#D946EF] flex items-center justify-center overflow-hidden border-2 border-[#D946EF]">
                    {currentArtist.photo_url ? (
                      <img src={`${BACKEND_URL}${currentArtist.photo_url}`} alt={currentArtist.name} className="w-full h-full object-cover" />
                    ) : (
                      <Mic2 className="w-10 h-10 text-white" />
                    )}
                  </div>
                  {/* Upload photo overlay */}
                  <input
                    type="file"
                    ref={photoInputRef}
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleUploadPhoto}
                  />
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </button>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">{currentArtist.name}</h2>
                  <p className="text-gray-400">{currentArtist.phone}</p>
                  <p className="text-xs text-[#D946EF] mt-1">Toca tu foto para cambiarla</p>
                </div>
                <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Points Status */}
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <p className="text-sm text-gray-400">Puntos Disponibles</p>
                  <p className="text-3xl font-bold text-[#D946EF]" data-testid="artist-points-value">
                    {Math.round(currentArtist.balance || 0).toLocaleString()} pts
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Membership + Payment Info (combined) */}
          <Card
            data-testid="membership-card"
            className={`border ${membershipStatus?.is_paid ? 'border-[#22C55E]/40 bg-[#22C55E]/10' : 'border-red-500/40 bg-red-500/10'}`}
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-white" />
                  <h3 className="font-bold text-white">Membresía Mensual</h3>
                </div>
                <span
                  data-testid="membership-status-badge"
                  className={`text-xs font-bold px-3 py-1 rounded-full ${membershipStatus?.is_paid ? 'bg-[#22C55E] text-black' : 'bg-red-500 text-white'}`}
                >
                  {membershipStatus?.is_paid ? '● PAGADO' : '● NO PAGADO'}
                </span>
              </div>
              <p className="text-sm text-gray-300">
                Membresía mensual
                {membershipStatus?.current_month ? ` (mes actual: ${formatMembershipMonth(membershipStatus.current_month)})` : ''}.
              </p>

              {membershipStatus?.has_pending && (
                <div className="p-2 bg-yellow-500/10 rounded-lg border border-yellow-500/30 text-xs text-yellow-400 flex items-center gap-2">
                  <Clock className="w-4 h-4 shrink-0" />
                  Pago en revisión: {membershipStatus.pending_months.map(formatMembershipMonth).join(", ")}
                </div>
              )}

              {membershipStatus?.paid_months?.length > 0 && (
                <p className="text-xs text-gray-400">
                  Meses pagados: <span className="text-[#22C55E]">{membershipStatus.paid_months.map(formatMembershipMonth).join(", ")}</span>
                </p>
              )}

              {/* Toggle payment form (hidden by default when already paid) */}
              <Button
                variant="outline"
                size="sm"
                data-testid="membership-toggle-form-btn"
                onClick={() => setPaymentFormOpen(prev => {
                  const current = prev !== null ? prev : !membershipStatus?.is_paid;
                  return !current;
                })}
                className="w-full border-white/15 text-gray-200 hover:bg-white/5"
              >
                {(paymentFormOpen !== null ? paymentFormOpen : !membershipStatus?.is_paid)
                  ? 'Ocultar formulario'
                  : (membershipStatus?.is_paid ? 'Pagar más meses' : 'Pagar membresía')}
              </Button>

              {(paymentFormOpen !== null ? paymentFormOpen : !membershipStatus?.is_paid) && (
                <div className="space-y-3">
                  {/* Payment account details */}
                  {paymentAccounts?.rd?.bank && (
                    <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-[#22C55E]" />
                        <span className="font-bold text-sm text-white">Paga a esta cuenta</span>
                        <span className="text-lg ml-auto">🇩🇴</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-400">Banco:</span> <span className="text-white font-medium">{paymentAccounts.rd.bank}</span></p>
                        <p><span className="text-gray-400">Cuenta:</span> <span className="text-white font-mono">{paymentAccounts.rd.account}</span></p>
                        <p><span className="text-gray-400">Nombre:</span> <span className="text-white">{paymentAccounts.rd.name}</span></p>
                        <p><span className="text-gray-400">Tipo:</span> <span className="text-white">{paymentAccounts.rd.type}</span></p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="membership-copy-account-btn"
                        className="mt-2 text-xs border-[#22C55E]/30 text-[#22C55E]"
                        onClick={() => {
                          navigator.clipboard.writeText(paymentAccounts.rd.account);
                          toast.success("Número de cuenta copiado");
                        }}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copiar Cuenta
                      </Button>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <Select value={membershipMonths} onValueChange={setMembershipMonths}>
                      <SelectTrigger data-testid="membership-months-select" className="w-36 bg-[#0a0a0a] border-white/10 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 12].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} {n > 1 ? 'meses' : 'mes'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <input
                      type="file"
                      ref={membershipFileInputRef}
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => handleSubmitMembership(e.target.files?.[0], false)}
                    />
                    <Button
                      data-testid="membership-upload-btn"
                      onClick={() => membershipFileInputRef.current?.click()}
                      disabled={submittingMembership}
                      className="flex-1 bg-[#22C55E] hover:bg-[#22C55E]/80 text-black"
                    >
                      {submittingMembership ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      Subir Comprobante
                    </Button>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Transfiere a la cuenta de arriba y sube el comprobante. El administrador lo verificará.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comprar Puntos Adicionales - SEPARATE CARD */}
          <Card
            data-testid="buy-points-card"
            className="border border-[#D946EF]/40 bg-[#D946EF]/5"
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Coins className="w-5 h-5 text-[#D946EF]" />
                  <h3 className="font-bold text-white">Comprar Puntos</h3>
                </div>
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#D946EF]/20 text-[#D946EF] border border-[#D946EF]/40">
                  OPCIONAL
                </span>
              </div>
              <p className="text-sm text-gray-300">
                Compra puntos adicionales para usar en lanzamientos, campañas y radio.
              </p>

              <Button
                variant="outline"
                size="sm"
                data-testid="buy-points-toggle-btn"
                onClick={() => setPointsPurchaseOpen(prev => !prev)}
                className="w-full border-[#D946EF]/30 text-[#D946EF] hover:bg-[#D946EF]/10"
              >
                {pointsPurchaseOpen ? 'Ocultar formulario' : 'Comprar puntos'}
              </Button>

              {pointsPurchaseOpen && (
                <div className="space-y-3">
                  {/* Payment account details */}
                  {paymentAccounts?.rd?.bank && (
                    <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#D946EF]/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-[#D946EF]" />
                        <span className="font-bold text-sm text-white">Paga a esta cuenta</span>
                        <span className="text-lg ml-auto">🇩🇴</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-400">Banco:</span> <span className="text-white font-medium">{paymentAccounts.rd.bank}</span></p>
                        <p><span className="text-gray-400">Cuenta:</span> <span className="text-white font-mono">{paymentAccounts.rd.account}</span></p>
                        <p><span className="text-gray-400">Nombre:</span> <span className="text-white">{paymentAccounts.rd.name}</span></p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="buy-points-copy-account-btn"
                        className="mt-2 text-xs border-[#D946EF]/30 text-[#D946EF]"
                        onClick={() => {
                          navigator.clipboard.writeText(paymentAccounts.rd.account);
                          toast.success("Número de cuenta copiado");
                        }}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copiar Cuenta
                      </Button>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm text-gray-300">¿Cuántos puntos quieres comprar?</Label>
                    <Input
                      type="number"
                      data-testid="buy-points-amount-input"
                      placeholder="Ej: 500"
                      value={requestedPoints}
                      onChange={(e) => setRequestedPoints(e.target.value)}
                      className="bg-[#0a0a0a] border-[#D946EF]/20 text-white"
                    />
                    <p className="text-[11px] text-gray-500">
                      Indica la cantidad de puntos que deseas. El admin confirmará al aprobar.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="file"
                      ref={pointsFileInputRef}
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => handleSubmitMembership(e.target.files?.[0], true)}
                    />
                    <Button
                      data-testid="buy-points-upload-btn"
                      onClick={() => pointsFileInputRef.current?.click()}
                      disabled={submittingMembership || !requestedPoints}
                      className="flex-1 bg-[#D946EF] hover:bg-[#c026d3]"
                    >
                      {submittingMembership ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      Subir Comprobante de Puntos
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Send Song/Video Link */}
          <Card data-testid="artist-link-card" className="bg-[#121212] border-white/10">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Youtube className="w-5 h-5 text-[#FF0000]" />
                <h3 className="font-bold text-white">Enviar Link de Canción o Video</h3>
              </div>
              <p className="text-sm text-gray-400">
                Comparte el enlace de YouTube de tu canción o video. El administrador lo revisará.
              </p>
              <Button
                data-testid="artist-open-link-dialog-btn"
                onClick={() => setShowLinkSubmit(true)}
                className="w-full bg-[#FF0000] hover:bg-[#cc0000] text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Enviar Link de YouTube
              </Button>

              {myLinkSubmissions.length > 0 && (
                <div className="space-y-2 pt-1">
                  <p className="text-xs text-gray-400">Mis enlaces enviados ({myLinkSubmissions.length})</p>
                  {myLinkSubmissions.map(link => (
                    <div key={link.id} data-testid={`artist-link-item-${link.id}`} className="flex items-center justify-between gap-2 bg-[#0a0a0a] rounded-lg p-2 border border-white/10">
                      <a
                        href={link.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-white truncate hover:text-[#FF6B6B] flex items-center gap-2 min-w-0"
                      >
                        <Youtube className="w-4 h-4 text-[#FF0000] shrink-0" />
                        <span className="truncate">{link.title}</span>
                      </a>
                      <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full ${link.status === 'reviewed' ? 'bg-[#22C55E] text-black' : 'bg-yellow-500 text-black'}`}>
                        {link.status === 'reviewed' ? 'Revisado' : 'Pendiente'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={showLinkSubmit} onOpenChange={setShowLinkSubmit}>
            <DialogContent className="bg-[#121212] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-[#FF0000]" />
                  Enviar Link de YouTube
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <Label className="text-sm">Título</Label>
                  <Input
                    data-testid="artist-link-title-input"
                    value={linkForm.title}
                    onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                    placeholder="Nombre de la canción o video"
                    className="bg-[#0a0a0a] border-white/10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Enlace de YouTube</Label>
                  <Input
                    data-testid="artist-link-url-input"
                    value={linkForm.youtube_url}
                    onChange={(e) => setLinkForm({ ...linkForm, youtube_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="bg-[#0a0a0a] border-white/10 mt-1"
                  />
                </div>
                <Button
                  data-testid="artist-link-submit-btn"
                  onClick={handleSubmitLink}
                  disabled={submittingLink || !linkForm.title.trim() || !linkForm.youtube_url.trim()}
                  className="w-full bg-[#FF0000] hover:bg-[#cc0000] text-white"
                >
                  {submittingLink ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Enviar
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Engagement Store (spend points) */}
          <Card data-testid="engagement-card" className="bg-gradient-to-br from-[#8B5CF6]/20 to-[#22C55E]/10 border-[#22C55E]/30 overflow-hidden">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#22C55E]" />
                <h3 className="font-bold text-white">Cambiar Puntos por Engagement</h3>
              </div>
              <p className="text-xs text-gray-400">
                Usa tus puntos para pedir suscriptores, comentarios, likes y visitas a tu link.
              </p>

              <div className="p-2 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/30 text-xs text-gray-300">
                <p className="font-bold text-[#22C55E] mb-1">Combo: 125 pts</p>
                <p>5 suscriptores + 5 comentarios + 5 likes. Puedes pedir varios combos.</p>
                <p className="mt-1 text-gray-400">Individual: Suscriptor 25 · Comentario 5 · Like 3 · Visita 1 (pts c/u)</p>
              </div>

              {/* Artist code (identifies the artist for received proofs) */}
              <div className="p-2 bg-[#D946EF]/10 rounded-lg border border-[#D946EF]/30 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-400">Tu código de artista</p>
                  <p className="font-bold text-[#D946EF] truncate" data-testid="artist-code-value">{currentArtist.artist_code || "— pídelo al admin —"}</p>
                </div>
                {currentArtist.artist_code && (
                  <Button
                    size="sm" variant="outline"
                    data-testid="artist-copy-code-btn"
                    className="text-xs border-[#D946EF]/40 text-[#D946EF] shrink-0"
                    onClick={() => { navigator.clipboard.writeText(currentArtist.artist_code); toast.success("Código copiado"); }}
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copiar
                  </Button>
                )}
              </div>

              <div>
                <Label className="text-sm">Tu link</Label>
                <Input
                  data-testid="engagement-link-input"
                  value={engagementForm.link}
                  onChange={(e) => setEngagementForm({ ...engagementForm, link: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  className="bg-[#0a0a0a] border-white/10 mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "combos", label: "Combos (125 c/u)" },
                  { key: "subscribers", label: "Suscriptores (25)" },
                  { key: "comments", label: "Comentarios (5)" },
                  { key: "likes", label: "Likes (3)" },
                  { key: "visits", label: "Visitas (1)" },
                ].map(item => (
                  <div key={item.key}>
                    <Label className="text-[11px] text-gray-400">{item.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      data-testid={`engagement-${item.key}-input`}
                      value={engagementForm[item.key]}
                      onChange={(e) => setEngagementForm({ ...engagementForm, [item.key]: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                      className="bg-[#0a0a0a] border-white/10 mt-1"
                    />
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between p-2 bg-[#0a0a0a] rounded-lg border border-white/10">
                <span className="text-sm text-gray-300">Total:</span>
                <span data-testid="engagement-total" className="text-lg font-bold text-[#22C55E]">{engagementTotal.toLocaleString()} pts</span>
              </div>

              <Button
                data-testid="engagement-submit-btn"
                onClick={handleCreateEngagementOrder}
                disabled={submittingEngagement || engagementTotal <= 0 || engagementTotal > Math.round(currentArtist.balance || 0)}
                className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black font-bold"
              >
                {submittingEngagement ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Star className="w-4 h-4 mr-1" />}
                {engagementTotal > Math.round(currentArtist.balance || 0) ? "Puntos insuficientes" : "Canjear Puntos"}
              </Button>

              {myEngagementOrders.length > 0 && (
                <div className="space-y-2 pt-1">
                  <p className="text-xs text-gray-400">Mis pedidos ({myEngagementOrders.length})</p>
                  {myEngagementOrders.slice(0, 5).map(o => (
                    <div key={o.id} className="bg-[#0a0a0a] rounded-lg p-2 border border-white/10 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[#D946EF] font-bold shrink-0" data-testid={`order-code-${o.id}`}>{o.order_code || `P-${o.id.slice(0,5).toUpperCase()}`}</span>
                        <span className="text-[#22C55E] font-bold shrink-0">-{o.points_cost} pts</span>
                      </div>
                      <p className="text-white truncate mt-0.5">{o.link}</p>
                      <p className="text-gray-400 mt-0.5">
                        {o.combos > 0 && `${o.combos} combo(s) · `}
                        {o.subscribers > 0 && `${o.subscribers} subs · `}
                        {o.comments > 0 && `${o.comments} coment. · `}
                        {o.likes > 0 && `${o.likes} likes · `}
                        {o.visits > 0 && `${o.visits} visitas`}
                      </p>
                      {(() => {
                        const total = o.total_units != null ? o.total_units : (o.combos * 15 + o.subscribers + o.comments + o.likes + o.visits);
                        const rec = o.received || 0;
                        return (
                          <p className="mt-1 font-bold">
                            <span className="text-[#22C55E]">Recibido: {rec}</span>
                            <span className="text-gray-500"> / {total}</span>
                            <span className="text-[#FF6B6B]"> · Falta: {Math.max(0, total - rec)}</span>
                          </p>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Launch Benefits Banner */}
          <Card className="bg-gradient-to-br from-[#FF6B6B]/20 via-[#FF8E53]/20 to-[#FFD700]/20 border-[#FF6B6B]/30 overflow-hidden">
            <CardContent className="p-0">
              <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53]">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Rocket className="w-5 h-5" />
                  🎬 Lanza Tu Video
                </h3>
                <p className="text-xs text-white/80">Impulsa tu carrera musical</p>
              </div>
              
              <div className="p-4">
                <p className="text-sm font-semibold text-white mb-3">✨ Tu lanzamiento incluye:</p>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-[#1E1E1E] p-3 rounded-lg text-center border border-[#22C55E]/30">
                    <p className="text-2xl font-bold text-[#22C55E]">50</p>
                    <p className="text-xs text-gray-400">Suscriptores</p>
                    <p className="text-[10px] text-[#22C55E]">a tu canal</p>
                  </div>
                  <div className="bg-[#1E1E1E] p-3 rounded-lg text-center border border-[#3B82F6]/30">
                    <p className="text-2xl font-bold text-[#3B82F6]">50</p>
                    <p className="text-xs text-gray-400">Comentarios</p>
                    <p className="text-[10px] text-[#3B82F6]">reales</p>
                  </div>
                  <div className="bg-[#1E1E1E] p-3 rounded-lg text-center border border-[#EC4899]/30">
                    <p className="text-2xl font-bold text-[#EC4899]">50</p>
                    <p className="text-xs text-gray-400">Likes</p>
                    <p className="text-[10px] text-[#EC4899]">en tu video</p>
                  </div>
                  <div className="bg-[#1E1E1E] p-3 rounded-lg text-center border border-[#F59E0B]/30">
                    <p className="text-2xl font-bold text-[#F59E0B]">50</p>
                    <p className="text-xs text-gray-400">Visitas</p>
                    <p className="text-[10px] text-[#F59E0B]">garantizadas</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-xs text-gray-300">
                  <p className="flex items-center gap-2">
                    <span className="text-[#FFD700]">✓</span> Publicación en la app LEYBA PRO
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-[#22C55E]">✓</span> Notificación a todos los usuarios
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-[#22C55E]">✓</span> Contador de asistentes en tiempo real
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-[#22C55E]">✓</span> Los usuarios pueden unirse al lanzamiento
                  </p>
                </div>
                
                <div className="mt-4 p-2 bg-[#8B5CF6]/20 rounded border border-[#8B5CF6]/30">
                  <p className="text-xs text-[#8B5CF6] font-semibold">💡 Opciones adicionales:</p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    • Promoción en Carrusel del inicio
                  </p>
                </div>

                {/* Create Launch Event Button - Inside Card */}
                <Dialog open={showCreateLaunch} onOpenChange={setShowCreateLaunch}>
                  <DialogTrigger asChild>
                    <Button className="w-full mt-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] hover:opacity-90 h-12 font-bold">
                      <Rocket className="w-5 h-5 mr-2" />
                      Anunciar Lanzamiento
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Rocket className="w-5 h-5 text-[#FF6B6B]" />
                        Crear Lanzamiento
                      </DialogTitle>
                    </DialogHeader>
              <form onSubmit={handleCreateLaunch} className="space-y-3">
                {/* Compact benefits summary */}
                <div className="p-3 bg-gradient-to-r from-[#FF6B6B]/20 to-[#FF8E53]/20 rounded-lg border border-[#FF6B6B]/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-[#FF6B6B]">📦 Incluye:</span>
                    <span className="text-xs text-gray-400">Beneficios</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1 text-center">
                    <div className="bg-[#1E1E1E] p-1 rounded">
                      <p className="text-sm font-bold text-[#22C55E]">50</p>
                      <p className="text-[8px] text-gray-400">Subs</p>
                    </div>
                    <div className="bg-[#1E1E1E] p-1 rounded">
                      <p className="text-sm font-bold text-[#22C55E]">50</p>
                      <p className="text-[8px] text-gray-400">Comentarios</p>
                    </div>
                    <div className="bg-[#1E1E1E] p-1 rounded">
                      <p className="text-sm font-bold text-[#22C55E]">50</p>
                      <p className="text-[8px] text-gray-400">Likes</p>
                    </div>
                    <div className="bg-[#1E1E1E] p-1 rounded">
                      <p className="text-sm font-bold text-[#22C55E]">1.5K</p>
                      <p className="text-[8px] text-gray-400">Visitas</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Título del Lanzamiento *</Label>
                  <Input
                    placeholder="Ej: ¡Nuevo Video Musical!"
                    className="bg-[#1E1E1E] border-white/10"
                    value={launchForm.title}
                    onChange={(e) => setLaunchForm({ ...launchForm, title: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>URL del Video *</Label>
                  <Input
                    placeholder="https://youtube.com/watch?v=..."
                    className="bg-[#1E1E1E] border-white/10"
                    value={launchForm.youtube_url}
                    onChange={(e) => setLaunchForm({ ...launchForm, youtube_url: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Descripción (opcional)</Label>
                  <Textarea
                    placeholder="Describe tu lanzamiento..."
                    className="bg-[#1E1E1E] border-white/10"
                    value={launchForm.description}
                    onChange={(e) => setLaunchForm({ ...launchForm, description: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha de Lanzamiento *</Label>
                    <Input
                      type="date"
                      className="bg-[#1E1E1E] border-white/10"
                      value={launchForm.launch_date}
                      onChange={(e) => setLaunchForm({ ...launchForm, launch_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hora de Lanzamiento *</Label>
                    <Input
                      type="time"
                      className="bg-[#1E1E1E] border-white/10"
                      value={launchForm.launch_time}
                      onChange={(e) => setLaunchForm({ ...launchForm, launch_time: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] h-12 font-bold"
                  disabled={(currentArtist.balance || 0) < 3500}
                >
                  {(currentArtist.balance || 0) < 3500 
                    ? "Saldo insuficiente"
                    : "🚀 Crear Lanzamiento"
                  }
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Button
            variant="outline"
            data-testid="wa-info-launch-btn"
            onClick={() => openWhatsAppInfo("Lanza Tu Video (lanzamiento)")}
            className="w-full mt-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Consultar por WhatsApp
          </Button>
              </div>
            </CardContent>
          </Card>

          {/* My Launches */}
          {myLaunches.length > 0 && (
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-[#FF6B6B]" />
                    Mis Lanzamientos ({myLaunches.length})
                  </div>
                  {/* Total participants summary */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#22C55E]/10 rounded-full border border-[#22C55E]/30">
                    <Users className="w-4 h-4 text-[#22C55E]" />
                    <span className="text-sm font-bold text-[#22C55E]">
                      {myLaunches.reduce((sum, l) => sum + (l.attendees_count || 0), 0)}
                    </span>
                    <span className="text-xs text-gray-400">total participantes</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {myLaunches.map((launch) => (
                  <div key={launch.id} className="p-3 bg-[#1E1E1E] rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold">{launch.title}</h4>
                          {launch.is_promoted && (
                            <span className="px-2 py-0.5 text-[10px] bg-[#FFD700] text-black rounded-full font-bold">
                              PROMOCIONADO
                            </span>
                          )}
                          {launch.has_plan1 && (
                            <span className="px-2 py-0.5 text-[10px] bg-[#8B5CF6] text-white rounded-full font-bold">
                              PLAN 1
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {new Date(launch.launch_datetime).toLocaleString("es-DO")}
                        </p>
                        {/* Participant Counter - Highlighted */}
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold ${
                            launch.attendees_count > 0 
                              ? 'bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/30' 
                              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                          }`}>
                            <Users className="w-3.5 h-3.5" />
                            <span>{launch.attendees_count}</span>
                            <span className="font-normal">participantes</span>
                          </div>
                          {launch.attendees_count > 0 && (
                            <span className="text-[10px] text-[#22C55E] animate-pulse">
                              🎉 ¡Usuarios unidos!
                            </span>
                          )}
                        </div>
                        
                        {/* Launch Actions */}
                        {launch.status === "upcoming" && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {!launch.is_promoted && (
                              <Button
                                onClick={() => handlePromoteLaunch(launch.id)}
                                size="sm"
                                className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black text-xs h-7"
                                disabled={(currentArtist.balance || 0) < 350}
                              >
                                <Star className="w-3 h-3 mr-1" />
                                Promocionar
                              </Button>
                            )}
                            {!launch.has_plan1 && (
                              <Button
                                onClick={() => handleCreatePlan1(launch.id, currentArtist.youtube_channel_url)}
                                size="sm"
                                className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-xs h-7"
                              >
                                <Megaphone className="w-3 h-3 mr-1" />
                                Crear Plan 1
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          launch.status === "upcoming" ? "bg-yellow-500 text-black" :
                          launch.status === "live" ? "bg-green-500 text-white" :
                          "bg-gray-500 text-white"
                        }`}>
                          {launch.status === "upcoming" ? "Próximo" : 
                           launch.status === "live" ? "En Vivo" : "Terminado"}
                        </span>
                        {launch.status === "upcoming" && launch.attendees_count === 0 && (
                          <Button
                            onClick={() => handleCancelLaunch(launch.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Cancelar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          
          {/* Share Campaign Info Banner - Enhanced */}
          <Card className="bg-gradient-to-br from-[#E1306C]/20 via-[#F77737]/15 to-[#FCAF45]/10 border-[#E1306C]/40 overflow-hidden">
            <CardContent className="p-0">
              {/* Header with gradient */}
              <div className="p-4 bg-gradient-to-r from-[#E1306C] to-[#F77737]">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl">
                    <Share2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">📢 Campaña de Compartir</h3>
                    <p className="text-xs text-white/80">Viraliza tu música en redes sociales</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                {/* Info highlight */}
                <div className="text-center mb-4 p-3 bg-[#22C55E]/10 rounded-xl border border-[#22C55E]/30">
                  <p className="text-lg font-bold text-[#22C55E]">50 compartidas garantizadas</p>
                  <p className="text-xs text-gray-400 mt-1">Viraliza tu música con usuarios reales</p>
                </div>

                {/* Duration info */}
                <div className="text-center mb-4 p-3 bg-[#FF6B6B]/10 rounded-xl border border-[#FF6B6B]/30">
                  <p className="text-sm font-bold text-[#22C55E]">⏱️ Duración: 2 horas 30 minutos</p>
                  <p className="text-xs text-gray-400 mt-1">La campaña se elimina automáticamente después de 30 min</p>
                </div>

                {/* What you get */}
                <p className="text-sm font-semibold text-white mb-3">🎯 50 usuarios compartirán tu música en:</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="flex items-center gap-2 p-2 bg-[#1877F2]/10 rounded-lg border border-[#1877F2]/30">
                    <span className="text-lg">📘</span>
                    <span className="text-sm text-[#1877F2] font-medium">Facebook</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-[#E1306C]/10 rounded-lg border border-[#E1306C]/30">
                    <span className="text-lg">📸</span>
                    <span className="text-sm text-[#E1306C] font-medium">Instagram</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg border border-white/20">
                    <span className="text-lg">🎵</span>
                    <span className="text-sm text-white font-medium">TikTok</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-[#25D366]/10 rounded-lg border border-[#25D366]/30">
                    <span className="text-lg">💬</span>
                    <span className="text-sm text-[#25D366] font-medium">WhatsApp</span>
                  </div>
                </div>

                {/* Create Share Campaign Button - Inside Card */}
                <Dialog open={showShareCampaign} onOpenChange={setShowShareCampaign}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-gradient-to-r from-[#E1306C] to-[#F77737] hover:opacity-90 h-12 font-bold text-white">
                      <Share2 className="w-5 h-5 mr-2" />
                      Crear Campaña de Compartir
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-[#E1306C]" />
                        Nueva Campaña de Compartir
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateShareCampaign} className="space-y-4">
                      {/* Explanation Banner */}
                      <div className="p-4 bg-gradient-to-r from-[#E1306C]/20 to-[#F77737]/20 rounded-lg border border-[#E1306C]/30">
                        <p className="text-sm font-bold text-white mb-2">📢 ¿Cómo funciona?</p>
                        <p className="text-xs text-gray-300 mb-3">
                          50 usuarios compartirán tu URL en la red social que elijas.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 bg-[#1877F2]/20 text-[#1877F2] text-xs rounded-full">📘 Facebook</span>
                          <span className="px-2 py-1 bg-[#E1306C]/20 text-[#E1306C] text-xs rounded-full">📸 Instagram</span>
                          <span className="px-2 py-1 bg-black/30 text-white text-xs rounded-full">🎵 TikTok</span>
                          <span className="px-2 py-1 bg-[#25D366]/20 text-[#25D366] text-xs rounded-full">💬 WhatsApp</span>
                        </div>
                      </div>

                      <div className="p-4 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/30">
                        <p className="text-lg font-bold text-[#22C55E] text-center">
                          50 compartidas garantizadas
                        </p>
                        <p className="text-xs text-gray-400 text-center mt-1">Usuarios reales compartirán tu música</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Título de la Campaña *</Label>
                        <Input
                          placeholder="Ej: ¡Comparte mi nuevo video!"
                          className="bg-[#1E1E1E] border-white/10"
                          value={shareCampaignForm.title}
                          onChange={(e) => setShareCampaignForm({ ...shareCampaignForm, title: e.target.value })}
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>URL a Compartir *</Label>
                        <Input
                          placeholder="https://youtube.com/watch?v=..."
                          className="bg-[#1E1E1E] border-white/10"
                          value={shareCampaignForm.share_url}
                          onChange={(e) => setShareCampaignForm({ ...shareCampaignForm, share_url: e.target.value })}
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Plataforma</Label>
                        <select
                          className="w-full bg-[#1E1E1E] border border-white/10 rounded-md p-2 text-white"
                          value={shareCampaignForm.platform}
                          onChange={(e) => setShareCampaignForm({ ...shareCampaignForm, platform: e.target.value })}
                        >
                          <option value="instagram">Instagram</option>
                          <option value="facebook">Facebook</option>
                          <option value="tiktok">TikTok</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="twitter">Twitter/X</option>
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Descripción</Label>
                        <Textarea
                          placeholder="Instrucciones para los usuarios..."
                          className="bg-[#1E1E1E] border-white/10"
                          value={shareCampaignForm.description}
                          onChange={(e) => setShareCampaignForm({ ...shareCampaignForm, description: e.target.value })}
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-[#E1306C] to-[#F77737] h-12 font-bold"
                        disabled={(currentArtist.balance || 0) < 4500}
                      >
                        {(currentArtist.balance || 0) < 4500
                          ? "Saldo insuficiente"
                          : "Crear Campaña"
                        }
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <Button
                  variant="outline"
                  data-testid="wa-info-share-btn"
                  onClick={() => openWhatsAppInfo("Campaña de Compartir")}
                  className="w-full mt-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Consultar por WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* My Share Campaigns */}
          {myShareCampaigns.length > 0 && (
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#E1306C]" />
                  Mis Campañas de Compartir
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {myShareCampaigns.map((campaign) => (
                  <div key={campaign.id} className="p-3 bg-[#1E1E1E] rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold">{campaign.title}</h4>
                        <p className="text-xs text-gray-400 capitalize">{campaign.platform}</p>
                        <p className="text-xs text-[#22C55E]">
                          {campaign.shares_count}/{campaign.max_users || "∞"} usuarios
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        campaign.is_active ? "bg-[#22C55E] text-black" : "bg-gray-500 text-white"
                      }`}>
                        {campaign.is_active ? "Activa" : "Completada"}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Radio Submission Card - NEW */}
          <Card className="bg-gradient-to-br from-[#FFD700]/20 via-[#DAA520]/15 to-[#B8860B]/10 border-[#FFD700]/40 overflow-hidden">
            <CardContent className="p-0">
              {/* Header with gradient */}
              <div className="p-4 bg-gradient-to-r from-[#FFD700] to-[#B8860B]">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl">
                    <Radio className="w-7 h-7 text-black" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-black">🎵 Subir a Radio LEYBA</h3>
                    <p className="text-sm text-black/70">Tu música en rotación 24/7</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-gray-300">
                    ¡Sube tu música a la radio de LEYBA PRO! Tu canción estará en rotación constante para todos los usuarios.
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#FFD700]">✓</span> Rotación 24/7 en la plataforma
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#FFD700]">✓</span> Exposición a todos los usuarios
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#FFD700]">✓</span> Tu nombre en cada reproducción
                  </div>
                </div>
                
                <div className="p-3 bg-[#1E1E1E] rounded-lg">
                  <p className="text-lg font-bold text-[#FFD700]">Tu música en la radio</p>
                  <p className="text-xs text-gray-400">Consulta los detalles con el admin</p>
                </div>
                
                {/* Submit Button */}
                <Dialog open={showRadioSubmit} onOpenChange={setShowRadioSubmit}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-gradient-to-r from-[#FFD700] to-[#B8860B] text-black font-bold h-12 text-base rounded-xl hover:opacity-90 transition-all">
                      <Music className="w-5 h-5 mr-2" />
                      Subir MP3 a la Radio
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Radio className="w-5 h-5 text-[#FFD700]" />
                        Subir Canción a Radio LEYBA
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Título de la canción</Label>
                        <Input
                          placeholder="Ej: Mi Nueva Canción"
                          className="bg-[#1E1E1E] border-white/10"
                          value={radioSubmitForm.title}
                          onChange={(e) => setRadioSubmitForm({ ...radioSubmitForm, title: e.target.value })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Archivo MP3</Label>
                        <input
                          type="file"
                          ref={radioFileInputRef}
                          accept=".mp3,audio/mpeg"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              setRadioSubmitForm({ ...radioSubmitForm, file: e.target.files[0] });
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10"
                          onClick={() => radioFileInputRef.current?.click()}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {radioSubmitForm.file ? radioSubmitForm.file.name : "Seleccionar MP3"}
                        </Button>
                      </div>
                      
                      <div className="p-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg">
                        <p className="text-sm text-[#FFD700] font-bold">Envío de canción</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Después de subir tu canción, deberás enviar el comprobante de pago.
                        </p>
                      </div>
                      
                      <Button
                        onClick={handleSubmitRadioTrack}
                        disabled={uploadingRadio || !radioSubmitForm.title || !radioSubmitForm.file}
                        className="w-full bg-gradient-to-r from-[#FFD700] to-[#B8860B] text-black font-bold"
                      >
                        {uploadingRadio ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo...</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" /> Enviar Canción</>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  variant="outline"
                  data-testid="wa-info-radio-btn"
                  onClick={() => openWhatsAppInfo("Subir canción a Radio LEYBA")}
                  className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Consultar por WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* My Radio Submissions */}
          {myRadioSubmissions.length > 0 && (
            <Card className="bg-[#121212] border-[#FFD700]/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Radio className="w-5 h-5 text-[#FFD700]" />
                  Mis Envíos a la Radio ({myRadioSubmissions.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {myRadioSubmissions.map((submission) => (
                  <div key={submission.id} className="p-3 bg-[#1E1E1E] rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-white">{submission.title}</h4>
                        <p className="text-xs text-gray-400">
                          {new Date(submission.created_at).toLocaleDateString()}
                        </p>
                        {submission.rejection_reason && (
                          <p className="text-xs text-red-400 mt-1">
                            Razón: {submission.rejection_reason}
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        submission.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        submission.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        submission.status === 'pending_review' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {submission.status === 'approved' ? '✓ En Radio' :
                         submission.status === 'rejected' ? '✗ Rechazada' :
                         submission.status === 'pending_review' ? 'En Revisión' :
                         'Pendiente Pago'}
                      </span>
                    </div>
                    
                    {/* Payment Proof Upload for pending_payment */}
                    {submission.status === 'pending_payment' && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <input
                          type="file"
                          id={`radio-proof-${submission.id}`}
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleUploadRadioPaymentProof(submission.id, e.target.files[0]);
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={() => document.getElementById(`radio-proof-${submission.id}`).click()}
                          disabled={uploadingRadio}
                          className="w-full bg-[#FFD700] hover:bg-[#DAA520] text-black"
                        >
                          {uploadingRadio ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo...</>
                          ) : (
                            <><Camera className="w-4 h-4 mr-2" /> Subir Comprobante de Pago</>
                          )}
                        </Button>
                        <p className="text-xs text-center text-gray-400 mt-2">
                          Sube tu comprobante de pago
                        </p>
                      </div>
                    )}
                    
                    {/* Show payment proof if uploaded */}
                    {submission.payment_proof_url && (
                      <div className="mt-2">
                        <img 
                          src={`${BACKEND_URL}${submission.payment_proof_url}`}
                          alt="Comprobante"
                          className="w-full h-20 object-cover rounded-lg cursor-pointer"
                          onClick={() => window.open(`${BACKEND_URL}${submission.payment_proof_url}`, '_blank')}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Social Links Section - NEW */}
          <Card className="bg-gradient-to-br from-[#8B5CF6]/20 via-[#D946EF]/15 to-[#EC4899]/10 border-purple-500/40 overflow-hidden">
            <CardContent className="p-0">
              {/* Header */}
              <div className="p-4 bg-gradient-to-r from-[#8B5CF6] to-[#D946EF]">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl">
                    <Share2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">📱 Gana Seguidores en Redes</h3>
                    <p className="text-xs text-white/80">Los usuarios te seguirán por recompensas</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 space-y-4">
                <p className="text-sm text-gray-300">
                  ¡Sube tus redes sociales! Los usuarios ganarán dinero por seguirte, aumentando tu base de fans.
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm p-2 bg-[#1E1E1E] rounded-lg">
                    <span className="text-[#E1306C]">📷</span> Instagram
                  </div>
                  <div className="flex items-center gap-2 text-sm p-2 bg-[#1E1E1E] rounded-lg">
                    <span className="text-[#1877F2]">👤</span> Facebook
                  </div>
                  <div className="flex items-center gap-2 text-sm p-2 bg-[#1E1E1E] rounded-lg">
                    <span className="text-white">🎵</span> TikTok
                  </div>
                  <div className="flex items-center gap-2 text-sm p-2 bg-[#1E1E1E] rounded-lg">
                    <span className="text-[#FF0000]">▶️</span> YouTube
                  </div>
                </div>
                
                <div className="p-3 bg-[#1E1E1E] rounded-lg">
                  <p className="text-lg font-bold text-[#FFD700]">Más seguidores reales</p>
                  <p className="text-xs text-gray-400">Consulta los detalles con el admin</p>
                </div>
                
                {/* Submit Social Link Dialog */}
                <Dialog open={showSocialLinkSubmit} onOpenChange={setShowSocialLinkSubmit}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] text-white font-bold h-12 text-base rounded-xl hover:opacity-90 transition-all">
                      <Plus className="w-5 h-5 mr-2" />
                      Agregar Red Social
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1a1a1a] border-purple-500/30 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-[#D946EF]">
                        <Share2 className="w-5 h-5" />
                        Agregar Red Social
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Plataforma</Label>
                        <Select 
                          value={socialLinkForm.platform} 
                          onValueChange={(v) => setSocialLinkForm({...socialLinkForm, platform: v})}
                        >
                          <SelectTrigger className="bg-[#121212] border-white/20 mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1a] border-white/20">
                            <SelectItem value="instagram">📷 Instagram</SelectItem>
                            <SelectItem value="facebook">👤 Facebook</SelectItem>
                            <SelectItem value="tiktok">🎵 TikTok</SelectItem>
                            <SelectItem value="youtube">▶️ YouTube</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Nombre de Usuario</Label>
                        <Input
                          placeholder="@tunombre"
                          value={socialLinkForm.username}
                          onChange={(e) => setSocialLinkForm({...socialLinkForm, username: e.target.value})}
                          className="bg-[#121212] border-white/20 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label>URL del Perfil</Label>
                        <Input
                          placeholder="https://instagram.com/tunombre"
                          value={socialLinkForm.profile_url}
                          onChange={(e) => setSocialLinkForm({...socialLinkForm, profile_url: e.target.value})}
                          className="bg-[#121212] border-white/20 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label>Descripción (opcional)</Label>
                        <Input
                          placeholder="Ej: Mi canal de música urbana"
                          value={socialLinkForm.description}
                          onChange={(e) => setSocialLinkForm({...socialLinkForm, description: e.target.value})}
                          className="bg-[#121212] border-white/20 mt-1"
                        />
                      </div>
                      
                      <Button
                        onClick={handleSubmitSocialLink}
                        disabled={submittingSocialLink || !socialLinkForm.username || !socialLinkForm.profile_url}
                        className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] text-white font-bold h-12 rounded-xl"
                      >
                        {submittingSocialLink ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
                        ) : (
                          <><Send className="w-4 h-4 mr-2" /> Enviar Solicitud</>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  variant="outline"
                  data-testid="wa-info-social-btn"
                  onClick={() => openWhatsAppInfo("Ganar seguidores en redes")}
                  className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Consultar por WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* My Social Links Submissions */}
          {mySocialLinks.length > 0 && (
            <Card className="bg-[#121212] border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-[#D946EF]" />
                  Mis Redes Sociales ({mySocialLinks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {mySocialLinks.map((link) => (
                  <div key={link.id} className="p-3 bg-[#1E1E1E] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-lg ${
                          link.platform === 'instagram' ? 'text-[#E1306C]' :
                          link.platform === 'facebook' ? 'text-[#1877F2]' :
                          link.platform === 'youtube' ? 'text-[#FF0000]' :
                          'text-white'
                        }`}>
                          {link.platform === 'instagram' ? '📷' :
                           link.platform === 'facebook' ? '👤' :
                           link.platform === 'youtube' ? '▶️' : '🎵'}
                        </span>
                        <span className="font-medium capitalize">{link.platform}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        link.status === 'approved' ? 'bg-[#22C55E]/20 text-[#22C55E]' :
                        link.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {link.status === 'approved' ? '✓ Aprobado' :
                         link.status === 'rejected' ? '✗ Rechazado' : '⏳ Pendiente'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400">@{link.username}</p>
                    {link.status === 'rejected' && link.rejection_reason && (
                      <p className="text-xs text-red-400 mt-1">Razón: {link.rejection_reason}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Video Campaign Info Banner - Enhanced */}
          <Card className="bg-gradient-to-br from-[#FF0000]/20 via-[#CC0000]/15 to-[#990000]/10 border-red-500/40 overflow-hidden">
            <CardContent className="p-0">
              {/* Header with gradient */}
              <div className="p-4 bg-gradient-to-r from-[#FF0000] to-[#CC0000]">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl">
                    <Youtube className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">📺 Campaña de Video</h3>
                    <p className="text-xs text-white/80">Impulsa tu video con engagement real</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                {/* Info highlight */}
                <div className="text-center mb-4 p-3 bg-[#22C55E]/10 rounded-xl border border-[#22C55E]/30">
                  <p className="text-lg font-bold text-[#22C55E]">Engagement real en tu video</p>
                  <p className="text-xs text-gray-400 mt-1">Campaña de 3 días</p>
                </div>

                {/* What you get */}
                <p className="text-sm font-semibold text-white mb-3">🎯 Los usuarios harán en tu video:</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="flex items-center gap-2 p-2 bg-red-500/10 rounded-lg border border-red-500/30">
                    <span className="text-lg">👆</span>
                    <span className="text-sm text-white font-medium">Suscribirse</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-red-500/10 rounded-lg border border-red-500/30">
                    <span className="text-lg">👍</span>
                    <span className="text-sm text-white font-medium">Dar Like</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-red-500/10 rounded-lg border border-red-500/30">
                    <span className="text-lg">💬</span>
                    <span className="text-sm text-white font-medium">Comentar</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-red-500/10 rounded-lg border border-red-500/30">
                    <span className="text-lg">👀</span>
                    <span className="text-sm text-white font-medium">Ver 10x/día</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs mb-4">
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">⏱️ Dura 3 días</span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">🔄 Renovable</span>
                </div>

                {/* Create Video Campaign Button - Inside Card */}
                <Dialog open={showUploadVideo} onOpenChange={setShowUploadVideo}>
                  <DialogTrigger asChild>
                    <Button data-testid="upload-video-btn" className="w-full bg-gradient-to-r from-[#FF0000] to-[#CC0000] hover:opacity-90 h-12 font-bold text-white">
                      <Youtube className="w-5 h-5 mr-2" />
                      Comprar Campaña de Video
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Youtube className="w-5 h-5 text-red-500" />
                        Nueva Campaña de Video
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUploadVideo} className="space-y-4">
                      {/* Cost Info */}
                      <div className="p-4 bg-gradient-to-r from-[#FF0000]/20 to-[#CC0000]/20 rounded-lg border border-red-500/30">
                        <p className="text-lg font-bold text-white mb-2">📺 Campaña de Video</p>
                        <p className="text-xs text-gray-300 mb-3">
                          Los usuarios harán estas tareas en tu video:
                        </p>
                        <ul className="text-xs text-gray-300 space-y-1">
                          <li>✅ Suscribirse a tu canal</li>
                          <li>✅ Dar like al video</li>
                          <li>✅ Dejar un comentario</li>
                          <li>✅ Ver el video 10 veces/día por 3 días</li>
                        </ul>
                      </div>
                      
                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                        <p className="text-xs text-yellow-400">
                          ⏱️ La campaña dura <strong>3 días</strong>. Puedes renovarla después.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Título de la Campaña</Label>
                        <Input data-testid="video-title" placeholder="Mi nuevo sencillo 2025" className="bg-[#1E1E1E] border-white/10" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>URL del Video</Label>
                        <div className="relative">
                          <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                          <Input data-testid="video-youtube-url" placeholder="https://youtube.com/watch?v=..." className="pl-10 bg-[#1E1E1E] border-white/10" value={videoForm.youtube_url} onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })} required />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Descripción (Opcional)</Label>
                        <Textarea data-testid="video-description" placeholder="Descripción de tu campaña..." className="bg-[#1E1E1E] border-white/10" value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} />
                      </div>
                      
                      <Button type="submit" data-testid="submit-video" className="w-full bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white h-12 font-bold">
                        <Youtube className="w-5 h-5 mr-2" />
                        Crear Campaña
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <Button
                  variant="outline"
                  data-testid="wa-info-video-btn"
                  onClick={() => openWhatsAppInfo("Campaña de Video")}
                  className="w-full mt-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Consultar por WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Add Balance Button */}
          <Dialog open={showAddBalance} onOpenChange={setShowAddBalance}>
            <DialogTrigger asChild>
              <Button className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] h-12">
                <Wallet className="w-5 h-5 mr-2" />
                Recargar Saldo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10">
              <DialogHeader>
                <DialogTitle>Recargar Saldo</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/20">
                  <p className="text-sm text-[#22C55E]">
                    💰 Tu saldo actual: <strong>RD${(currentArtist.balance || 0).toFixed(2)}</strong>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Monto a recargar (RD$)</Label>
                  <Input
                    type="number"
                    placeholder="Ej: 500"
                    className="bg-[#1E1E1E] border-white/10"
                    value={addBalanceAmount}
                    onChange={(e) => setAddBalanceAmount(e.target.value)}
                  />
                </div>
                <div className="p-3 bg-[#1E1E1E] rounded-lg text-sm">
                  <p className="text-xs text-gray-400">
                    Consulta los precios de los servicios con el administrador por WhatsApp.
                  </p>
                </div>
                <Button onClick={handleAddBalanceRequest} className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black">
                  Solicitar Recarga
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Upload Payment Receipt - Prominent Button */}
          {myPayments.filter(p => p.status === 'pending').length > 0 && (
            <Card className="bg-gradient-to-r from-[#8B5CF6]/20 to-[#D946EF]/20 border-purple-500/40">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Upload className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Tienes pagos pendientes</p>
                      <p className="text-sm text-gray-400">
                        {myPayments.filter(p => p.status === 'pending').length} pago(s) esperando comprobante
                      </p>
                    </div>
                  </div>
                  <Button
                    className="bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] text-white font-bold"
                    onClick={() => {
                      const pendingPayment = myPayments.find(p => p.status === 'pending');
                      if (pendingPayment) {
                        setCurrentPayment(pendingPayment);
                        setShowPaymentDialog(true);
                      }
                    }}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Subir Comprobante
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* My Video Campaigns */}
          <div>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Youtube className="w-5 h-5 text-red-500" />
              Mis Campañas de Video ({myVideos.length})
            </h3>
            {myVideos.length > 0 ? (
              <div className="space-y-3">
                {myVideos.map((campaign) => (
                  <Card key={campaign.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-16 rounded-lg bg-[#1E1E1E] overflow-hidden">
                          <img src={campaign.thumbnail_url || `https://img.youtube.com/vi/${campaign.youtube_url.split("v=")[1]?.split("&")[0]}/default.jpg`} alt={campaign.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold line-clamp-1">{campaign.title}</h4>
                          <p className="text-sm text-gray-400">{campaign.completed_users || 0} usuarios completaron</p>
                          <div className="flex items-center gap-2 mt-1">
                            {campaign.is_active ? (
                              <span className="text-xs bg-[#22C55E]/20 text-[#22C55E] px-2 py-0.5 rounded-full">Activa</span>
                            ) : (
                              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">Expirada</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-6 text-center text-gray-400">
                  No has creado campañas de video aún
                </CardContent>
              </Card>
            )}
          </div>

          {/* My Payments Section */}
          {myPayments.length > 0 && (
            <div className="mt-6">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#8B5CF6]" />
                Mis Pagos Pendientes
              </h3>
              <div className="space-y-2">
                {myPayments.filter(p => p.status !== 'completed').slice(0, 5).map((payment) => (
                  <Card key={payment.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm">{payment.description}</p>
                        <p className="text-xs text-gray-400">RD${payment.amount?.toLocaleString()}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          payment.status === 'confirming' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {payment.status === 'confirming' ? '⏳ En revisión' : '📋 Pendiente'}
                        </span>
                      </div>
                      {payment.status === 'pending' && (
                        <Button
                          size="sm"
                          className="bg-[#8B5CF6]"
                          onClick={() => {
                            setCurrentPayment(payment);
                            setShowPaymentDialog(true);
                          }}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Subir
                        </Button>
                      )}
                      {payment.receipt_url && (
                        <a href={`${BACKEND_URL}${payment.receipt_url}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-white/20">
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Payment Dialog */}
        {showPaymentDialog && currentPayment && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
            <Card className="bg-[#121212] border-white/10 w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#8B5CF6]" />
                  Subir Comprobante de Pago
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Payment Info */}
                <div className="p-4 bg-gradient-to-r from-[#8B5CF6]/20 to-[#D946EF]/20 rounded-lg border border-purple-500/30">
                  <p className="text-lg font-bold text-white mb-1">{currentPayment.description}</p>
                  <p className="text-2xl font-bold text-[#22C55E]">RD${currentPayment.amount?.toLocaleString()}</p>
                </div>
                
                {/* Bank Info */}
                <div className="p-3 bg-[#1E1E1E] rounded-lg border border-white/10">
                  <p className="text-xs text-gray-400 mb-2">📋 Datos para transferencia:</p>
                  <p className="text-sm"><strong>Banco:</strong> Banco Popular</p>
                  <p className="text-sm"><strong>Cuenta:</strong> 123-456789-0</p>
                  <p className="text-sm"><strong>Nombre:</strong> E1 Media RD</p>
                  <p className="text-sm"><strong>RNC:</strong> 123-45678-9</p>
                </div>
                
                {/* Instructions */}
                <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                  <p className="text-xs text-yellow-400 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    Realiza la transferencia y sube una captura del comprobante. Tu campaña se activará cuando el administrador apruebe el pago.
                  </p>
                </div>
                
                {/* Upload Section */}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={receiptInputRef}
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleReceiptUpload(currentPayment.id, e.target.files[0]);
                    }
                  }}
                />
                
                <Button
                  onClick={() => receiptInputRef.current?.click()}
                  className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] text-white h-12 font-bold"
                  disabled={uploadingReceipt}
                >
                  {uploadingReceipt ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Camera className="w-5 h-5 mr-2" />
                      📸 Subir Comprobante de Pago
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full border-white/20"
                  onClick={() => {
                    setShowPaymentDialog(false);
                    setCurrentPayment(null);
                  }}
                >
                  Cancelar
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // Artist Auth (not logged in)
  return (
    <div className="min-h-screen pb-20 p-4 animate-fadeInUp" data-testid="artist-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D946EF] to-[#8B5CF6] flex items-center justify-center mx-auto mb-4">
            <Mic2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold gradient-text">Portal de Artistas</h1>
          <p className="text-gray-400 mt-2">Sube tu música y videos</p>
        </div>

        {/* Auth Card */}
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-6">
            <Tabs value={authTab} onValueChange={setAuthTab}>
              <TabsList className="grid w-full grid-cols-2 bg-[#1E1E1E]">
                <TabsTrigger value="login">Iniciar Sesión</TabsTrigger>
                <TabsTrigger value="register">Registrarse</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input data-testid="artist-login-phone" type="tel" placeholder="809-123-4567" className="pl-10 bg-[#1E1E1E] border-white/10" value={loginForm.phone} onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input data-testid="artist-login-password" type={showPassword ? "text" : "password"} placeholder="••••••••" className="pl-10 pr-10 bg-[#1E1E1E] border-white/10" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" data-testid="artist-login-submit" className="w-full bg-[#D946EF] hover:bg-[#D946EF]/80 h-12 text-lg font-bold rounded-xl">
                    Entrar
                  </Button>
                  
                  {/* Forgot Password */}
                  <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
                    <DialogTrigger asChild>
                      <button type="button" className="w-full text-center text-sm text-[#FFD700] hover:text-[#DAA520] mt-3">
                        ¿Olvidaste tu contraseña?
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#121212] border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-center">Recuperar Contraseña</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <p className="text-sm text-gray-400 text-center">
                          Ingresa tu número de teléfono y te conectaremos con el administrador por WhatsApp para recuperar tu contraseña.
                        </p>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            type="tel"
                            placeholder="809-123-4567"
                            className="pl-10 bg-[#1E1E1E] border-white/10"
                            value={forgotPhone}
                            onChange={(e) => setForgotPhone(e.target.value)}
                          />
                        </div>
                        <Button
                          onClick={handleForgotPassword}
                          disabled={loading}
                          className="w-full bg-[#FFD700] hover:bg-[#DAA520] text-black font-bold"
                        >
                          {loading ? "Enviando..." : "Contactar Administrador"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </form>
              </TabsContent>

              <TabsContent value="register" className="mt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre Artístico</Label>
                    <Input data-testid="artist-register-name" placeholder="Tu nombre artístico" className="bg-[#1E1E1E] border-white/10" value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input data-testid="artist-register-phone" type="tel" placeholder="809-123-4567" className="pl-10 bg-[#1E1E1E] border-white/10" value={registerForm.phone} onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input data-testid="artist-register-password" type={showPassword ? "text" : "password"} placeholder="••••••••" className="pl-10 pr-10 bg-[#1E1E1E] border-white/10" value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} required />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" data-testid="artist-register-submit" className="w-full bg-[#22C55E] hover:bg-[#22C55E]/80 text-black h-12 text-lg font-bold rounded-xl">
                    Crear Cuenta
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Artists Gallery */}
        {artists.length > 0 && (
          <div className="mt-8">
            <h3 className="text-xl font-bold mb-4" style={{
              background: 'linear-gradient(90deg, #FFD700, #DAA520)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Artistas en LEYBA PRO</h3>
            <div className="grid grid-cols-2 gap-4">
              {artists.slice(0, 4).map((artist) => (
                <Card 
                  key={artist.id} 
                  className="bg-[#121212] border-white/10 overflow-hidden cursor-pointer hover:border-[#D946EF]/50 transition-all"
                  onClick={() => setSelectedArtist(artist)}
                  data-testid={`artist-card-${artist.id}`}
                >
                  <CardContent className="p-0">
                    <div className="h-24 bg-gradient-to-br from-[#D946EF]/30 to-[#8B5CF6]/30 flex items-center justify-center">
                      {artist.photo_url ? (
                        <img src={artist.photo_url} alt={artist.name} className="w-full h-full object-cover" />
                      ) : (
                        <Mic2 className="w-10 h-10 text-white/50" />
                      )}
                    </div>
                    <div className="p-2">
                      <h4 className="font-bold text-sm line-clamp-1">{artist.name}</h4>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Artist Detail Modal */}
        <Dialog open={!!selectedArtist} onOpenChange={(open) => !open && setSelectedArtist(null)}>
          <DialogContent className="bg-[#121212] border-white/10 max-w-md">
            {selectedArtist && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-center text-xl font-bold" style={{
                    background: 'linear-gradient(90deg, #FFD700, #DAA520)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {selectedArtist.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Artist Photo */}
                  <div className="w-full aspect-square max-h-64 rounded-xl overflow-hidden bg-gradient-to-br from-[#D946EF]/30 to-[#8B5CF6]/30 flex items-center justify-center">
                    {selectedArtist.photo_url ? (
                      <img 
                        src={selectedArtist.photo_url} 
                        alt={selectedArtist.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Mic2 className="w-24 h-24 text-white/50" />
                    )}
                  </div>
                  
                  {/* Artist Bio */}
                  <div className="bg-[#1E1E1E] rounded-lg p-4">
                    <h4 className="text-sm font-bold text-[#D946EF] mb-2">Biografía</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {selectedArtist.bio || "Este artista aún no ha agregado una biografía."}
                    </p>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ArtistPage;
